import { useMemo } from 'react'
import {
  CellClassParams,
  CellStyle,
  EditableCallbackParams,
  ICellRendererParams,
  IDoesFilterPassParams,
  NewValueParams,
  RowNode,
  ValueFormatterParams,
  ValueGetterParams,
  ValueParserParams,
  ValueSetterParams,
} from 'ag-grid-community'
import {
  AggregateField,
  WbsItemStatus,
} from '../../../domain/entity/WbsItemEntity'
import { dateVoService } from '../../../domain/value-object/DateVO'
import { WbsItemTypeVO } from '../../../domain/value-object/WbsItemTypeVO'
import {
  CustomEnumCombinationDirection,
  CustomEnumValue,
  FunctionProperty,
  PropertyType,
} from '../../../lib/commons/appFunction'
import { filterValuesByCombination } from '../../../lib/commons/customEnum'
import { getLabel } from '../../../lib/commons/i18nLabel'
import ProjectMemberAPI, {
  ProjectMemberProps,
} from '../../../lib/functions/projectMember'
import { ProjectPlanCumulation } from '../../../lib/functions/projectPlan'
import Sprint, {
  FilterSortOrder,
  SprintDetail,
  SprintStatus,
  SprintStatusDisplayOrder,
} from '../../../lib/functions/sprint'
import { dateV2Service } from '../../../domain/value-object/DateV2'
import { formatSprintLabel } from '../../pages/ProductBacklog/components/SprintAssignmentDialog/types'
import { TagForWbsItem } from '../../../lib/functions/tag'
import { TeamProps } from '../../../lib/functions/team'
import {
  getWbsItemStatusColorCode,
  getWbsItemStatusDeepColorCode,
  getWbsItemStatusLabel,
  WbsItemRow,
} from '../../../lib/functions/wbsItem'
import { intl } from '../../../i18n'
import store from '../../../store'
import { Comment, CommentSummary } from '../../../store/comments'
import { BackgroundColor, TextColor } from '../../../styles/commonStyles'
import { colorPalette } from '../../style/colorPalette'
import { AttachmentSummary } from '../../../utils/attachment'
import BoolExpression from '../../../utils/boolExpression'
import { DateTerm } from '../../../utils/date'
import { toInteger, toNumber } from '../../../utils/number'
import objects from '../../../utils/objects'
import DateVO from '../../../vo/DateVO'
import {
  CustomEnumCellEditor,
  DateCellEditor,
  EntitySearchCellEditor,
  MultiAutocompleteCellEditor,
  TagCellEditor,
} from '../../containers/BulkSheetView/components/cellEditor'
import { IDateCellEditorParams } from '../../containers/BulkSheetView/components/cellEditor/DateCellEditor'
import {
  AttachmentCellRenderer,
  CustomEnumCellRenderer,
  DefaultCellRenderer,
  EntitySearchCellRenderer,
  ProjectPlanActionCellRenderer,
  ProjectPlanStatusCellRenderer,
  TicketTypeCellRenderer,
} from '../../containers/BulkSheetView/components/cellRenderer'
import { NumberCellRenderer } from '../../containers/BulkSheetView/components/cellRenderer/NumberCellRenderer'
import { TagCellRenderer } from '../../containers/BulkSheetView/components/cellRenderer/TagCellRenderer'
import { TicketTypeCellValue } from '../../containers/BulkSheetView/components/cellRenderer/TicketTypeCellRenderer'
import {
  ClientSideNumberFilter,
  ClientSideSelectFilter,
  ClientSideTextFilter,
  ServerSideUuidFilter,
} from '../../containers/BulkSheetView/components/filter'
import { ListRow } from '../../containers/BulkSheetView/model'
import { ColumnType } from '../../containers/commons/AgGrid'
import MultiAutocompleteCell from '../../containers/commons/AgGrid/components/cell/common/multiAutocomplete'
import { multiAutocompleteCellFilterValueFormatter } from '../../containers/commons/AgGrid/components/cell/common/multiAutocomplete/cellEditor'
import attachmentCellFilter from '../../containers/commons/AgGrid/components/cell/custom/attachment/attachmentCellFilter'
import CommentCell from '../../containers/commons/AgGrid/components/cell/custom/comment'
import CommentCellFilter from '../../containers/commons/AgGrid/components/cell/custom/comment/CommentCellFilter'
import { DetailCellFilter } from '../../containers/commons/AgGrid/components/cell/custom/detail'
import WbsItemBasicCellRenderer from '../../containers/commons/AgGrid/components/cell/custom/wbsItemName/basicCellRenderer'
import {
  WbsItemCellRenderer,
  Props as WbsItemCellRendererProps,
} from '../../containers/commons/AgGrid/components/cell/custom/wbsItemName/wbsItemCellRenderer'
import { WbsItemTypeCellValue } from '../../containers/commons/AgGrid/components/cell/custom/wbsItemType'
import { customHeaderTemplate } from '../../containers/commons/AgGrid/components/header/CustomHeader'
import { EntitySearchValue } from '../../containers/meta/repositories'
import { BulkSheetProperty } from './bulksheetProperty'
import {
  floatValueFormatter,
  useCustomEnumColumn,
  useMultiLineTextColum,
} from './common'
import { commonValueSetter, defaultValueSetter } from '.'
import { WbsItemCodeFormat } from '../../../lib/functions/project'
import { useProjectPrivateContext } from '../../context/projectContext'
import { WbsItemStatusCellRenderer } from '../../containers/BulkSheetView/components/cellRenderer/WbsItemStatusCellRenderer'
import { WbsItemStatusCellEditor } from '../../containers/BulkSheetView/components/cellEditor/WbsItemStatusCellEditor'
import { WbsItemSearchRow } from '../../pages/WbsItemSearch/wbsItemSearchOptions'

export const useUuid = (): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field: 'uuid',
      hide: true,
      suppressColumnsToolPanel: true,
      filter: ServerSideUuidFilter,
    }),
    []
  )
  return property
}

// Base Infomation -----

export const useCode = (field: string): BulkSheetProperty => {
  const { project } = useProjectPrivateContext()
  const property = useMemo(() => {
    const uiMeta = {
      propertyType: PropertyType.Text,
      charactersAllowed: 'a-zA-Z0-9_-',
      maxLength: 32,
    } as Partial<FunctionProperty>

    return {
      field,
      headerName: intl.formatMessage({ id: 'wbsItem.property.code' }),
      hide: true,
      pinned: true,
      width: 100,
      editable: params =>
        params.data.added &&
        project.wbsItemCodeFormat === WbsItemCodeFormat.RANDOM,
      cellRenderer: DefaultCellRenderer,
      cellRendererParams: {
        uiMeta,
      },
      cellEditorParams: {
        uiMeta,
      },
      filter: ClientSideTextFilter,
      floatingFilter: true,
    }
  }, [field, project])
  return property
}

export const useType = <TRow extends ListRow>({
  field,
  getBaseWbsItemType,
  getTypeIndex,
}: {
  field: string
  getBaseWbsItemType: (row: TRow | undefined) => WbsItemTypeVO | undefined
  getTypeIndex: (row: TRow | undefined) => number | undefined
}): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      headerName: intl.formatMessage({ id: 'wbsItem.property.type' }),
      hide: true,
      pinned: true,
      width: 100,
      editable: false,
      valueGetter: (params: ValueGetterParams<TRow>) => {
        const baseType = getBaseWbsItemType(params.data)
        const typeIndex = getTypeIndex(params.data)
        if (!baseType) return {}
        return {
          wbsItemType: baseType,
          typeIndex,
        } as WbsItemTypeCellValue
      },
      valueFormatter: (params: ValueFormatterParams<TRow>) =>
        getBaseWbsItemType(params.data)?.getNameWithSuffix() ?? '',
      filter: ClientSideSelectFilter,
      filterParams: {
        getValue: (option: WbsItemTypeCellValue) =>
          option?.wbsItemType?.getNameWithSuffix() ?? '',
        getLabel: (option: WbsItemTypeCellValue) =>
          option?.wbsItemType?.getNameWithSuffix() ?? '',
        sortValues: (
          uiMeta: FunctionProperty,
          options: WbsItemTypeCellValue[],
          context: { [key: string]: CustomEnumValue[] }
        ) => {
          return options.sort(sortWbsItemType)
        },
      },
      comparator: sortWbsItemType,
      floatingFilter: true,
    }),
    [field, getBaseWbsItemType, getTypeIndex]
  )
  return property as BulkSheetProperty
}

export const useTicketType = <TRow extends ListRow>({
  field,
  getBaseWbsItemType,
  getTypeIndex,
}: {
  field: string
  getBaseWbsItemType: (row: TRow | undefined) => WbsItemTypeVO | undefined
  getTypeIndex: (row: TRow | undefined) => number | undefined
}): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      headerName: intl.formatMessage({ id: 'wbsItem.property.ticketType' }),
      hide: true,
      pinned: true,
      width: 100,
      editable: false,
      sortable: true,
      cellRenderer: TicketTypeCellRenderer,
      valueGetter: (params: ValueGetterParams<TRow>) => {
        const baseType = getBaseWbsItemType(params.data)
        const typeIndex = getTypeIndex(params.data)
        if (!baseType || !baseType.isTicket()) return
        return {
          wbsItemType: baseType,
          typeIndex,
        } as TicketTypeCellValue
      },
      valueFormatter: (params: ValueFormatterParams<TRow>) =>
        getBaseWbsItemType(params.data)?.getNameWithSuffix() ?? '',
      filter: ClientSideSelectFilter,
      filterParams: {
        getValue: (option: TicketTypeCellValue) =>
          option?.wbsItemType?.name ?? '',
        getLabel: (option: TicketTypeCellValue) =>
          option?.wbsItemType?.name ?? '',
        sortValues: (
          uiMeta: FunctionProperty,
          options: TicketTypeCellValue[],
          context: { [key: string]: CustomEnumValue[] }
        ) => {
          return options.sort(sortWbsItemType)
        },
      },
      floatingFilter: true,
      comparator: sortWbsItemType,
    }),
    [field, getBaseWbsItemType, getTypeIndex]
  )
  return property as BulkSheetProperty
}

export const useStatus = ({
  field,
  subStatusField,
  statusCombinedValuePath,
  statusCustomEnumCode = 'status',
  substatusCustomEnumCode = 'substatus',
}: {
  field: string
  subStatusField: string
  statusCombinedValuePath: (combinedEnumCode: string) => string | undefined
  statusCustomEnumCode?: string
  substatusCustomEnumCode?: string
}): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      headerName: intl.formatMessage({ id: 'wbsItem.property.status' }),
      pinned: true,
      width: 150,
      editable: true,
      type: [ColumnType.customEnum],
      cellEditor: CustomEnumCellEditor,
      cellEditorParams: {
        customEnumCode: statusCustomEnumCode,
        combinedValuePath: statusCombinedValuePath,
      },
      valueSetter: (params: ValueSetterParams) => {
        return commonValueSetter(params, (params: ValueSetterParams) => {
          objects.setValue(
            params.data,
            field,
            params.newValue ?? WbsItemStatus.TODO
          )
          // Update substatus
          const { combinedValuePath } = params.colDef.cellEditorParams
          const substatusOptions = filterValuesByCombination(
            params.context[substatusCustomEnumCode],
            code => objects.getValue(params.data, combinedValuePath?.(code)),
            true
          )
          objects.setValue(
            params.data,
            subStatusField,
            substatusOptions?.[0]?.value
          )
        })
      },
      cellRenderer: ProjectPlanStatusCellRenderer,
      cellStyle: (params: CellClassParams) => {
        const status = objects.getValue(params.data, field)
        if (!status) {
          return { backgroundColor: '#F7F7F7' }
        }
        return {
          backgroundColor: `${getWbsItemStatusColorCode(status)}`,
        }
      },
      filter: ClientSideSelectFilter,
      floatingFilter: true,
      filterParams: {
        valueGetter: (params: IDoesFilterPassParams) => {
          const status = objects.getValue(params.node.data, field)
          if (!status) return
          return {
            status,
            backgroundColor: `${getWbsItemStatusDeepColorCode(status)}`,
          } as StatusFilterParams
        },
        getValue: (v?: StatusFilterParams) => v?.status,
        getLabel: (v?: StatusFilterParams) =>
          v ? getWbsItemStatusLabel(v.status) : v,
        sortValues: (uiMeta, options: StatusFilterParams[]) => {
          return options.sort(
            (valueA: StatusFilterParams, valueB: StatusFilterParams) =>
              wbsItemStatusComparator(valueA.status, valueB.status)
          )
        },
      },
      comparator: wbsItemStatusComparator,
    }),
    [
      field,
      subStatusField,
      statusCustomEnumCode,
      substatusCustomEnumCode,
      statusCombinedValuePath,
    ]
  )
  return property
}

export const useStatusV2 = ({
  field,
  subStatusField,
  statusCombinedValuePath,
  statusCustomEnumCode = 'status',
  substatusCustomEnumCode = 'substatus',
  onMoreOptionsClick,
}: {
  field: string
  subStatusField: string
  statusCombinedValuePath: (combinedEnumCode: string) => string | undefined
  statusCustomEnumCode?: string
  substatusCustomEnumCode?: string
  onMoreOptionsClick?: (
    target: EventTarget | undefined,
    row: WbsItemSearchRow | any
  ) => void
}): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      headerName: intl.formatMessage({ id: 'wbsItem.property.status' }),
      pinned: true,
      width: 125,
      minWidth: 125,
      resizable: false,
      editable: true,
      type: [ColumnType.customEnum],
      cellEditor: WbsItemStatusCellEditor,
      cellEditorParams: {
        customEnumCode: statusCustomEnumCode,
        combinedValuePath: statusCombinedValuePath,
      },
      valueSetter: (params: ValueSetterParams) => {
        return commonValueSetter(params, (params: ValueSetterParams) => {
          objects.setValue(
            params.data,
            field,
            params.newValue ?? WbsItemStatus.TODO
          )
          // Update substatus
          const { combinedValuePath } = params.colDef.cellEditorParams
          const substatusOptions = filterValuesByCombination(
            params.context[substatusCustomEnumCode],
            code => objects.getValue(params.data, combinedValuePath?.(code)),
            true
          )
          objects.setValue(
            params.data,
            subStatusField,
            substatusOptions?.[0]?.value
          )
        })
      },
      cellRenderer: WbsItemStatusCellRenderer,
      cellRendererParams: {
        onMoreOptionsClick: onMoreOptionsClick || undefined,
      },
      filter: ClientSideSelectFilter,
      floatingFilter: true,
      filterParams: {
        valueGetter: (params: IDoesFilterPassParams) => {
          const status = objects.getValue(params.node.data, field)
          if (!status) return
          return {
            status,
            backgroundColor: `${getWbsItemStatusDeepColorCode(status)}`,
          } as StatusFilterParams
        },
        getValue: (v?: StatusFilterParams) => v?.status,
        getLabel: (v?: StatusFilterParams) =>
          v ? getWbsItemStatusLabel(v.status) : v,
        sortValues: (uiMeta, options: StatusFilterParams[]) => {
          return options.sort(
            (valueA: StatusFilterParams, valueB: StatusFilterParams) =>
              wbsItemStatusComparator(valueA.status, valueB.status)
          )
        },
      },
      comparator: wbsItemStatusComparator,
    }),
    [
      field,
      subStatusField,
      statusCustomEnumCode,
      substatusCustomEnumCode,
      statusCombinedValuePath,
    ]
  )
  return property
}

export const useSubstatus = ({
  field,
  statusCombinedValuePath,
  customEnumCode = 'substatus',
}: {
  field: string
  statusCombinedValuePath: (combinedEnumCode: string) => string | undefined
  customEnumCode?: string
}): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      headerName: intl.formatMessage({ id: 'wbsItem.property.substatus' }),
      pinned: true,
      hide: true,
      width: 90,
      editable: true,
      type: [ColumnType.customEnum],
      cellEditor: CustomEnumCellEditor,
      cellEditorParams: {
        customEnumCode,
        combinedValuePath: statusCombinedValuePath,
      },
      valueSetter: customEnumValueSetter,
      cellRenderer: CustomEnumCellRenderer,
      cellRendererParams: { customEnumCode: customEnumCode },
      filter: ClientSideSelectFilter,
      floatingFilter: true,
      filterParams: {
        valueGetter: ({ node, context }: { node: RowNode; context: any }) => {
          const options = context ? context[customEnumCode] : []
          const value = objects.getValue(node.data, field)
          return options?.find(o => o.value === value)
        },
        getValue: (v?: CustomEnumValue) => v?.value,
        getLabel: (v?: CustomEnumValue) =>
          v?.nameI18n ? getLabel(v.nameI18n) : v?.name,
      },
    }),
    [field, customEnumCode, statusCombinedValuePath]
  )
  return property
}

export const useCommentSummaryAction = <TRow extends ListRow>({
  field,
  getCommentSummary,
  getWbsItem,
}: {
  field: string
  getCommentSummary: (row: TRow | undefined) => CommentSummary | undefined
  // TODO: The retrieved wbsItem is specified in the mapRowDataForCommentHeader in the ProjectPlanActionCellRenderer.
  // The argument of mapRowDataForCommentHeader is WbsItemRow
  getWbsItem: (row: TRow | undefined) => WbsItemRow | undefined
}): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      headerName: intl.formatMessage({ id: 'wbsItem.property.action' }),
      hide: false,
      pinned: true,
      sortable: false,
      width: 100,
      cellRenderer: ProjectPlanActionCellRenderer,
      cellRendererParams: {
        getCommentSummary,
        getWbsItem,
        openComment: true,
      },
      valueGetter: (params: ValueGetterParams<TRow>) =>
        getCommentSummary(params.data)?.hasComment ||
        getWbsItem(params.data)?.uuid, // This valueGetter is to notify the action cell of the necessary that it needs to be refreshed.
      filter: DetailCellFilter,
      floatingFilter: true,
    }),
    [field, getCommentSummary, getWbsItem]
  )
  return property
}

export const useAttachments = <TRow extends ListRow>({
  field,
  getWbsItemUuid,
  getAttachmentSummary,
}: {
  field: string
  getWbsItemUuid?: (node: RowNode<TRow>) => string | undefined
  getAttachmentSummary?: (row: TRow) => AttachmentSummary | undefined
}) => {
  const property = useMemo(
    () => ({
      field,
      headerName: intl.formatMessage({ id: 'wbsItem.property.attachments' }),
      pinned: true,
      width: 55,
      cellRenderer: AttachmentCellRenderer,
      filter: attachmentCellFilter,
      floatingFilter: true,
      cellRendererParams: {
        getWbsItemUuid,
        getAttachmentSummary,
      },
      comparator: (
        a: AttachmentSummary | undefined,
        b: AttachmentSummary | undefined
      ) => {
        if (!a && !b) return 0
        if (!a) return -1
        if (!b) return 1
        if (0 < a.totalAttachmentCount) return 1
        if (0 < b.totalAttachmentCount) return -1
        return 0
      },
    }),
    [field, getWbsItemUuid, getAttachmentSummary]
  )
  return property
}

export const useTags = <TRow extends ListRow>({
  field,
  getWbsItemProjectUuid,
}: {
  field: string
  getWbsItemProjectUuid: (data: TRow | undefined) => string | undefined
}): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      headerName: intl.formatMessage({ id: 'wbsItem.property.tags' }),
      width: 90,
      editable: true,
      type: [ColumnType.multiSelect],
      cellEditor: TagCellEditor,
      cellEditorParams: {
        projectUuidExtractor: getWbsItemProjectUuid,
      },
      cellRenderer: TagCellRenderer,
      cellRendererParams: {
        uiMeta: {},
      },
      filter: ClientSideSelectFilter,
      floatingFilter: true,
      filterParams: {
        valueFormatter: (params: ValueFormatterParams) => {
          return params.value
        },
        getValue: option => option?.uuid,
        getLabel: option => option?.name,
        sortValues: (uiMeta, options) => {
          return options.sort((a, b) => {
            if (a.projectUuid !== b.projectUuid) {
              return (a?.projectUuid ?? '').localeCompare(b?.projectUuid ?? '')
            }
            const tags = store.getState().tag[a.projectUuid]
            if (!tags) return options
            return (
              tags.findIndex(e => e.uuid === a.uuid) -
              tags.findIndex(e => e.uuid === b.uuid)
            )
          })
        },
      },
      comparator: sortTag,
    }),
    [field, getWbsItemProjectUuid]
  )
  return property
}

// Path ------
export const usePath = <TRow extends ListRow>(
  field: string,
  getWbsItemType: (row: TRow | undefined) => WbsItemTypeVO | undefined,
  getStale?: (row: TRow | undefined) => boolean | undefined
): BulkSheetProperty => {
  const property = useMemo<BulkSheetProperty>(
    () => ({
      field,
      headerName: intl.formatMessage({ id: 'wbsItem.property.path' }),
      hide: true,
      pinned: true,
      width: 120,
      filter: ClientSideTextFilter,
      floatingFilter: true,
      cellStyle: (params: CellClassParams<TRow>): CellStyle => {
        const wbsItemType = getWbsItemType(params.data)
        const isCalculating =
          !params.value && !!wbsItemType && !wbsItemType.isWorkgroup()
        return {
          direction: 'ltr',
          ...(isCalculating ? { color: colorPalette.monotone[4] } : {}),
        }
      },
      cellRendererParams: {
        stale: (params: ICellRendererParams<TRow>): boolean =>
          !!getStale?.(params.data),
        staleMessage: intl.formatMessage({ id: 'viewModel.stale.path' }),
      },
      valueFormatter: (params: ValueFormatterParams<TRow>) => {
        const wbsItemType = getWbsItemType(params.data)
        if (!wbsItemType || wbsItemType.isWorkgroup()) {
          return params.value
        }
        return params.value
          ? params.value
          : intl.formatMessage({ id: 'wbsItemPath.calculating' })
      },
    }),
    [field]
  )
  return property
}

export const useParentWbsItem = <TRow extends ListRow>({
  field,
  getParentWbsItem,
}: {
  field: string
  getParentWbsItem: (row: TRow | undefined) => Partial<WbsItemRow> | undefined
}): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      headerName: intl.formatMessage({ id: 'wbsItem.property.parentWbsItem' }),
      pinned: true,
      hide: true,
      editable: false,
      valueGetter: (params: ValueGetterParams) => {
        return getParentWbsItem(params.data)
      },
      cellRenderer: WbsItemBasicCellRenderer,
      cellRendererParams: {
        uiMeta: {} as Partial<FunctionProperty>,
      },
      filter: ClientSideTextFilter,
      floatingFilter: true,
      filterValueGetter: (params: ValueGetterParams) => {
        return getParentWbsItem(params.data)?.displayName
      },
      comparator: (
        valueA: Partial<WbsItemRow>,
        valueB: Partial<WbsItemRow>
      ) => {
        return (valueA?.displayName ?? '').localeCompare(
          valueB?.displayName ?? ''
        )
      },
    }),
    [field, getParentWbsItem]
  )
  return property
}

// Name ------
export const useDisplayName = ({
  field,
  wbsItemCellRendererParams,
}: {
  field: string
  wbsItemCellRendererParams?: Partial<WbsItemCellRendererProps>
}): BulkSheetProperty => {
  const property = useMemo(() => {
    const uiMeta = {
      propertyType: PropertyType.Text,
      maxLength: 256,
      requiredIf: BoolExpression.of(true),
    } as Partial<FunctionProperty>

    return {
      field,
      headerName: intl.formatMessage({ id: 'wbsItem.property.displayName' }),
      width: 200,
      pinned: true,
      cellRenderer: WbsItemCellRenderer,
      cellRendererParams: {
        suppressCount: true,
        suppressDoubleClickExpand: true,
        uiMeta,
        ...wbsItemCellRendererParams,
      },
      cellEditorParams: {
        uiMeta,
      },
      editable: true,
      valueGetter: (params: ValueGetterParams) => {
        return objects.getValue(params.data, field) ?? ''
      },
      filter: ClientSideTextFilter,
      floatingFilter: true,
    }
  }, [field, wbsItemCellRendererParams])
  return property
}

export const usePriority = ({
  field,
  customEnumCode = 'priority',
  customEnumOptions,
}: {
  field: string
  customEnumCode?: string
  customEnumOptions?: CustomEnumValue[] // TODO: GridOption.context cannot be obtained from ColDef.comprator, so pass it as an argument.
}): BulkSheetProperty => {
  const cosutomEnumColumn = useCustomEnumColumn({
    field,
    customEnumCode,
    customEnumOptions,
  })
  const property = useMemo(
    () => ({
      ...cosutomEnumColumn,
      headerName: intl.formatMessage({ id: 'wbsItem.property.priority' }),
    }),
    [cosutomEnumColumn]
  )
  return property
}

export const useDifficulty = ({
  field,
  customEnumCode = 'difficulty',
  customEnumOptions,
}: {
  field: string
  customEnumCode?: string
  customEnumOptions?: CustomEnumValue[] // TODO: GridOption.context cannot be obtained from ColDef.comprator, so pass it as an argument.
}): BulkSheetProperty => {
  const cosutomEnumColumn = useCustomEnumColumn({
    field,
    customEnumCode,
    customEnumOptions,
  })
  const property = useMemo(
    () => ({
      ...cosutomEnumColumn,
      headerName: intl.formatMessage({ id: 'wbsItem.property.difficulty' }),
    }),
    [cosutomEnumColumn]
  )
  return property
}

// Dscription Commont ------
export const useDescription = (field: string): BulkSheetProperty => {
  const multiLineColumnDef = useMultiLineTextColum(field)
  const property = useMemo(
    () => ({
      ...multiLineColumnDef,
      headerName: intl.formatMessage({ id: 'wbsItem.property.description' }),
      hide: true,
      cellRendererParams: {
        uiMeta: {
          propertyType: PropertyType.MultiLineText,
          maxLength: 10000,
        } as Partial<FunctionProperty>,
      },
    }),
    [multiLineColumnDef]
  )
  return property
}

export const useLatestComment = (field: string): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      headerName: intl.formatMessage({ id: 'wbsItem.property.latestComment' }),
      hide: true,
      cellRenderer: CommentCell.cellRenderer,
      filter: CommentCellFilter,
      floatingFilter: true,
      comparator: (
        valueA: Comment | undefined,
        valueB: Comment | undefined
      ) => {
        const valA: number = valueA?.createdAt ?? 0
        const valB: number = valueB?.createdAt ?? 0
        return valA - valB
      },
    }),
    [field]
  )
  return property
}

// Assignment -----
export const useTeam = ({
  field,
  optionKey = 'team',
  gridOptionContext,
  clearSprint,
}: {
  field: string
  optionKey?: string
  gridOptionContext: { [key: string]: any } // TODO: GridOption.context cannot be obtained from ColDef.comprator, so pass it as an argument.
  clearSprint?: (params: ValueSetterParams) => void
}): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      headerName: intl.formatMessage({ id: 'wbsItem.property.team' }),
      width: 120,
      editable: true,
      type: [ColumnType.autocomplete],
      cellRenderer: EntitySearchCellRenderer,
      cellEditor: EntitySearchCellEditor,
      cellEditorParams: { entity: optionKey },
      suppressKeyboardEvent: params =>
        (!params.editing &&
          ['Delete', 'Backspace'].includes(params.event.key)) ||
        (params.editing && params.event.key === 'Enter'),
      valueSetter: (params: ValueSetterParams) => {
        return commonValueSetter(params, (params: ValueSetterParams) => {
          const oldTeam: TeamProps | undefined = params.oldValue
          const newTeam: TeamProps | undefined = params.newValue
          if (oldTeam?.uuid !== newTeam?.uuid && clearSprint) {
            clearSprint(params)
          }
        })
      },
      filter: ClientSideSelectFilter,
      floatingFilter: true,
      filterParams: {
        valueGetter: ({ node, context }) => {
          const val = objects.getValue(node.data, field)
          return typeof val === 'string' ? undefined : val // Exclude unselected data
        },
        getValue: (v?: TeamProps) => v?.uuid,
        getLabel: (v?: TeamProps) => v?.displayName,
        sortValues: (uiMeta, options) => {
          if (!gridOptionContext) return options
          const allOptions: TeamProps[] = gridOptionContext[optionKey]
          if (!allOptions) return options
          return options.sort((a: TeamProps, b: TeamProps) => {
            return (
              allOptions.findIndex(e => e.uuid === a?.uuid) -
              allOptions.findIndex(e => e.uuid === b?.uuid)
            )
          })
        },
      },
      comparator: (a: TeamProps, b: TeamProps) => {
        const options: TeamProps[] = gridOptionContext[optionKey]
        if (!options) {
          return (a?.displayName ?? '').localeCompare(b?.displayName ?? '')
        }
        return (
          options.findIndex(e => e.uuid === a?.uuid) -
          options.findIndex(e => e.uuid === b?.uuid)
        )
      },
    }),
    [field, optionKey, gridOptionContext]
  )
  return property
}

export const useAccountable = <TRow extends ListRow>({
  field,
  optionKey = 'member',
  getWbsItemProjectUuid,
}: {
  field: string
  optionKey?: string
  getWbsItemProjectUuid?: (row: TRow | undefined) => string | undefined
}): BulkSheetProperty => {
  const projectMemberColumn = useProjectMember({
    field,
    optionKey,
    getWbsItemProjectUuid,
  })
  const property = useMemo(
    () => ({
      ...projectMemberColumn,
      headerName: intl.formatMessage({ id: 'wbsItem.property.accountable' }),
    }),
    [projectMemberColumn]
  )
  return property
}

export const useResponsible = <TRow extends ListRow>({
  field,
  optionKey = 'member',
  getWbsItemProjectUuid,
}: {
  field: string
  optionKey?: string
  getWbsItemProjectUuid?: (row: TRow | undefined) => string | undefined
}): BulkSheetProperty => {
  const projectMemberColumn = useProjectMember({
    field,
    optionKey,
    getWbsItemProjectUuid,
  })
  const property = useMemo(
    () => ({
      ...projectMemberColumn,
      headerName: intl.formatMessage({ id: 'wbsItem.property.responsible' }),
    }),
    [projectMemberColumn]
  )
  return property
}

export const useAssignee = <TRow extends ListRow>({
  field,
  optionKey = 'member',
  getWbsItemProjectUuid,
}: {
  field: string
  optionKey?: string
  getWbsItemProjectUuid?: (row: TRow | undefined) => string | undefined
}): BulkSheetProperty => {
  const projectMemberColumn = useProjectMember({
    field,
    optionKey,
    getWbsItemProjectUuid,
  })
  const property = useMemo(
    () => ({
      ...projectMemberColumn,
      headerName: intl.formatMessage({ id: 'wbsItem.property.assignee' }),
    }),
    [projectMemberColumn]
  )
  return property
}

export const useWatchers = <TRow extends ListRow>({
  field,
}: {
  field: string
}): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      headerName: intl.formatMessage({ id: 'wbsItem.property.watcher' }),
      hide: true,
      width: 120,
      editable: true,
      type: [ColumnType.autocomplete],
      valueSetter: (params: ValueSetterParams) => {
        const watchars: ProjectMemberProps[] = params.newValue
        if (watchars) {
          params.newValue = watchars.sort(
            (valueA: ProjectMemberProps, valueB: ProjectMemberProps) =>
              (valueA?.name ?? '').localeCompare(valueB?.name ?? '')
          )
        }
        return defaultValueSetter(params)
      },
      cellRenderer: MultiAutocompleteCell.cellRenderer,
      cellRendererParams: {
        showIcon: true,
        sortValue: (data: any) => {
          if (!data) return data
          return data.sort((a, b) => {
            return (a?.name ?? '').localeCompare(b?.name ?? '')
          })
        },
      },
      cellEditor: MultiAutocompleteCellEditor,
      cellEditorParams: {
        search: async (text: string, projectUuid: string) =>
          ProjectMemberAPI.search(text, { projectUuid }),
        label: option => option.name,
        sortOptions: options =>
          options.sort((a, b) => {
            return (a?.name ?? '').localeCompare(b?.name ?? '')
          }),
      },
      filter: ClientSideSelectFilter,
      floatingFilter: true,
      filterParams: {
        valueFormatter: multiAutocompleteCellFilterValueFormatter,
        getValue: option => new EntitySearchValue(option).toString(),
        getLabel: option => new EntitySearchValue(option).toString(),
        sortValues: (uiMeta, options: ProjectMemberProps[]) => {
          return options.sort((a, b) => {
            return (a?.name ?? '').localeCompare(b?.name ?? '')
          })
        },
      },
      comparator: sortWatchers,
    }),
    [field]
  )
  return property
}

// Estimated -----
// TODO: The types of the Workload variables for WbsItemRow and NewWbsItemRow are different, so they cannot be commonized.
// TODO: Should use NewWbsItemRow?
export const useEstimatedWorkLoadDeliverable = <TRow extends ListRow>({
  field,
  getWbsItemType,
}: {
  field: string
  getWbsItemType: (row: TRow | undefined) => WbsItemTypeVO | undefined
}): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      headerName: intl.formatMessage({
        id: 'wbsItem.property.estimated.deliverable',
      }),
      width: 80,
      editable: (params: EditableCallbackParams<TRow>) =>
        getWbsItemType(params.data)?.isDeliverable(),
      cellClass: 'ag-numeric-cell',
      cellStyle: { justifyContent: 'flex-end' },
      cellRenderer: NumberCellRenderer,
      cellRendererParams: {
        numberWithCommas: true,
        decimalPoints: WORKLOAD_VALUE_DIGITS,
      },
      cellEditorParams: {
        uiMeta: {
          propertyType: PropertyType.Number,
          charactersAllowed: '.0-9',
        } as Partial<FunctionProperty>,
      },
      valueParser: (params: ValueParserParams) => toNumber(params.newValue),
      filter: ClientSideNumberFilter,
      floatingFilter: true,
    }),
    [field, getWbsItemType]
  )
  return property as BulkSheetProperty
}

// TODO: The types of the Workload variables for WbsItemRow and NewWbsItemRow are different, so they cannot be commonized.
// TODO: Should use NewWbsItemRow?
export const useEstimatedWorkLoadTask = <TRow extends ListRow>({
  field,
  getWbsItemType,
}: {
  field: string
  getWbsItemType: (row: TRow | undefined) => WbsItemTypeVO | undefined
}): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      headerName: intl.formatMessage({
        id: 'wbsItem.property.estimated.task',
      }),
      width: 80,
      editable: (params: EditableCallbackParams<TRow>) =>
        getWbsItemType(params.data)?.isTask(),
      cellRenderer: NumberCellRenderer,
      cellRendererParams: {
        numberWithCommas: true,
        decimalPoints: WORKLOAD_VALUE_DIGITS,
      },
      cellEditorParams: {
        uiMeta: {
          propertyType: PropertyType.Number,
          charactersAllowed: '.0-9',
        } as Partial<FunctionProperty>,
      },
      cellClass: 'ag-numeric-cell',
      cellStyle: { justifyContent: 'flex-end' },
      valueParser: (params: ValueParserParams) => toNumber(params.newValue),
      filter: ClientSideNumberFilter,
      floatingFilter: true,
    }),
    [field, getWbsItemType]
  )
  return property as BulkSheetProperty
}

export const useEstimatedStoryPoint = (field: string): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      headerName: intl.formatMessage({
        id: 'wbsItem.property.estimated.storypoint',
      }),
      hide: true,
      width: 120,
      editable: true,
      cellClass: 'ag-numeric-cell',
      cellStyle: { justifyContent: 'flex-end' },
      cellRenderer: NumberCellRenderer,
      cellRendererParams: {
        numberWithCommas: true,
        decimalPoints: 0,
      },
      cellEditorParams: {
        uiMeta: {
          propertyType: PropertyType.Number,
        } as Partial<FunctionProperty>,
      },
      valueParser: (params: ValueParserParams) => toInteger(params.newValue),
      filter: ClientSideNumberFilter,
      floatingFilter: true,
    }),
    [field]
  )
  return property
}

// Progress -----
export const useEstimatedProgressRate = <TRow>(
  field: string,
  getWbsItem: (row: TRow) => WbsItemRow | undefined,
  getCumulation: (row: TRow) => ProjectPlanCumulation | undefined
): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      ...progressRateCellCommonColumnDef,
      field,
      headerName: intl.formatMessage({
        id: 'wbsItem.property.scheduledProgressRate',
      }),
      width: 90,
      valueGetter: (params: ValueGetterParams<TRow>) => {
        if (!params.data) {
          return ''
        }
        const wbsItem = getWbsItem(params.data)
        if (!wbsItem || (wbsItem.wbsItemType?.isTask() && !isDone(wbsItem))) {
          return ''
        }
        const cumulation = getCumulation(params.data)
        const earnedValue = getTaskPlannedValue(wbsItem, cumulation)
        const estimatedHour = getTaskEstimatedHour(wbsItem, cumulation)
        return getRate(earnedValue, estimatedHour)
      },
    }),
    [field]
  )
  return property
}

export const useProgressRate = <TRow>(
  field: string,
  getWbsItem: (row: TRow) => WbsItemRow | undefined,
  getCumulation: (row: TRow) => ProjectPlanCumulation | undefined
): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      ...progressRateCellCommonColumnDef,
      field,
      headerName: intl.formatMessage({
        id: 'wbsItem.property.progressRate',
      }),
      valueGetter: (params: ValueGetterParams<TRow>) => {
        if (!params.data) {
          return ''
        }
        const wbsItem = getWbsItem(params.data)
        if (!wbsItem || (wbsItem.wbsItemType?.isTask() && !isDone(wbsItem))) {
          return ''
        }
        const cumulation = getCumulation(params.data)
        const earnedValue = getTaskEarnedValue(wbsItem, cumulation)
        const estimatedHour = getTaskEstimatedHour(wbsItem, cumulation)
        return getRate(earnedValue, estimatedHour)
      },
    }),
    [field]
  )
  return property
}

export const usePlannedValue = <TRow>(
  field: string,
  getWbsItem: (row: TRow) => WbsItemRow | undefined,
  getCumulation: (row: TRow) => ProjectPlanCumulation | undefined
): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      ...progressCellCommonColumnDef,
      field,
      headerName: intl.formatMessage({
        id: 'wbsItem.property.scheduledToBeCompleted',
      }),
      valueGetter: (params: ValueGetterParams<TRow>) => {
        if (!params.data) {
          return ''
        }
        const wbsItem = getWbsItem(params.data)
        if (
          wbsItem?.wbsItemType?.isTask() &&
          (!isScheduledToBeDone(wbsItem) || isDiscard(wbsItem))
        ) {
          return ''
        }
        const cumulation = getCumulation(params.data)
        return formatEvm(
          getTaskPlannedValue(wbsItem, cumulation),
          params.context.workloadUnitState?.hoursPerSelectedUnit || 1
        )
      },
    }),
    [field]
  )
  return property
}

export const useEarnedValue = <TRow>(
  field: string,
  getWbsItem: (row: TRow) => WbsItemRow | undefined,
  getCumulation: (row: TRow) => ProjectPlanCumulation | undefined
): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      ...progressCellCommonColumnDef,
      field,
      headerName: intl.formatMessage({
        id: 'wbsItem.property.progress.earnedvalue',
      }),
      valueGetter: (params: ValueGetterParams<TRow>) => {
        if (!params.data) {
          return ''
        }
        const wbsItem = getWbsItem(params.data)
        if (wbsItem?.wbsItemType?.isTask() && !isDone(wbsItem)) {
          return ''
        }
        const cumulation = getCumulation(params.data)
        return (
          getTaskEarnedValue(wbsItem, cumulation) /
          (params.context.workloadUnitState?.hoursPerSelectedUnit || 1)
        )
      },
    }),
    [field]
  )
  return property
}

export const usePreceding = <TRow>(
  field: string,
  getWbsItem: (row: TRow) => WbsItemRow | undefined,
  getCumulation: (row: TRow) => ProjectPlanCumulation | undefined
): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      ...progressCellCommonColumnDef,
      field,
      headerName: intl.formatMessage({
        id: 'wbsItem.property.preceding',
      }),
      valueGetter: (params: ValueGetterParams<TRow>) => {
        if (!params.data) {
          return ''
        }
        const wbsItem = getWbsItem(params.data)
        const cumulation = getCumulation(params.data)
        return formatEvm(
          getPrecedingTaskWorkload(wbsItem, cumulation),
          params.context.workloadUnitState?.hoursPerSelectedUnit || 1
        )
      },
    }),
    [field]
  )
  return property
}

export const useDelayed = <TRow>(
  field: string,
  getWbsItem: (row: TRow) => WbsItemRow | undefined,
  getCumulation: (row: TRow) => ProjectPlanCumulation | undefined
): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      ...progressCellCommonColumnDef,
      field,
      headerName: intl.formatMessage({
        id: 'wbsItem.property.delayed',
      }),
      valueGetter: (params: ValueGetterParams<TRow>) => {
        if (!params.data) {
          return ''
        }
        const wbsItem = getWbsItem(params.data)
        const cumulation = getCumulation(params.data)
        return formatEvm(
          getDelayedTaskWorkload(wbsItem, cumulation),
          params.context.workloadUnitState?.hoursPerSelectedUnit || 1
        )
      },
    }),
    [field]
  )
  return property
}

export const useRemaining = <TRow>(
  field: string,
  getWbsItem: (row: TRow) => WbsItemRow | undefined,
  getCumulation: (row: TRow) => ProjectPlanCumulation | undefined
): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      ...progressCellCommonColumnDef,
      field,
      headerName: intl.formatMessage({
        id: 'wbsItem.property.remaining',
      }),
      valueGetter: (params: ValueGetterParams<TRow>) => {
        if (!params.data) {
          return ''
        }
        const wbsItem = getWbsItem(params.data)
        const cumulation = getCumulation(params.data)
        if (wbsItem?.wbsItemType?.isTask()) {
          return isDone(wbsItem) || isDiscard(wbsItem)
            ? 0
            : formatEvm(
                getTaskEstimatedHour(wbsItem, cumulation),
                params.context.workloadUnitState?.hoursPerSelectedUnit || 1
              )
        }
        return formatEvm(
          getTaskEstimatedHour(wbsItem, cumulation) -
            getTaskEarnedValue(wbsItem, cumulation),
          params.context.workloadUnitState?.hoursPerSelectedUnit || 1
        )
      },
    }),
    [field]
  )
  return property
}

// Productivity -----
export const useActualHour = <TRow extends ListRow>({
  field,
  getWbsItemType,
  getCumulation,
  getWbsItem,
}: {
  field: string
  getWbsItemType: (row: TRow | undefined) => WbsItemTypeVO | undefined
  getCumulation: (row: TRow | undefined) => ProjectPlanCumulation | undefined
  getWbsItem: (row: TRow | undefined) => WbsItemRow | undefined
}): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      hide: false,
      width: 120,
      headerName: intl.formatMessage({
        id: 'wbsItem.property.actualHour',
      }),
      cellRenderer: NumberCellRenderer,
      cellRendererParams: {
        numberWithCommas: true,
        decimalPoints: WORKLOAD_VALUE_DIGITS,
      },
      valueGetter: (params: ValueGetterParams<TRow>) => {
        const cumulation = getCumulation(params.data)
        const wbsItem = getWbsItem(params.data)
        if (!wbsItem || !cumulation) return
        const hour = getWbsItemType(params.data)?.isTask()
          ? wbsItem.actualHour || 0
          : cumulation.sumActualHour
        return (
          hour / (params.context.workloadUnitState?.hoursPerSelectedUnit || 1)
        )
      },
      valueFormatter: (params: ValueFormatterParams<TRow>) =>
        floatValueFormatter(params, '', WORKLOAD_VALUE_DIGITS),
      cellStyle: (params: CellClassParams<TRow>) => {
        const numberStyle = { justifyContent: 'flex-end' }
        const wbsItem = getWbsItem(params.data)
        const cumulation = getCumulation(params.data)
        if (!wbsItem || !cumulation) return numberStyle
        if (0 < (wbsItem.actualHour || 0)) {
          return {
            ...numberStyle,
            cursor: 'pointer',
            color: 'blue',
          }
        }
        return numberStyle
      },
      filter: ClientSideNumberFilter,
      floatingFilter: true,
    }),
    [field, getWbsItemType, getCumulation, getWbsItem]
  )
  return property
}

export const useCostVariance = <TRow>(
  field: string,
  getWbsItem: (row: TRow) => WbsItemRow | undefined,
  getCumulation: (row: TRow) => ProjectPlanCumulation | undefined
): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      ...evmCellCommonColumnDef,
      field,
      headerName: intl.formatMessage({
        id: 'wbsItem.property.costVariance',
      }),
      headerTooltip: intl.formatMessage({ id: 'evm.description.CV' }),
      headerComponentParams: {
        template: customHeaderTemplate({
          tooltip: intl.formatMessage({ id: 'evm.description.CV' }),
        }),
      },
      valueGetter: (params: ValueGetterParams<TRow>) => {
        if (!params.data) {
          return ''
        }
        const wbsItem = getWbsItem(params.data)
        if (!wbsItem || (wbsItem.wbsItemType?.isTask() && !isDone(wbsItem))) {
          return ''
        }
        const cumulation = getCumulation(params.data)
        const earnedValue = getTaskEarnedValue(wbsItem, cumulation)
        const actualCost = getActualHour(wbsItem, cumulation)
        return formatEvm(
          earnedValue - actualCost,
          params.context.workloadUnitState?.hoursPerSelectedUnit || 1
        )
      },
    }),
    [field]
  )
  return property
}

export const useCostPerformanceIndex = <TRow>(
  field: string,
  getWbsItem: (row: TRow) => WbsItemRow | undefined,
  getCumulation: (row: TRow) => ProjectPlanCumulation | undefined
): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      ...evmRateCellCommonColumnDef,
      hide: false,
      field,
      headerName: intl.formatMessage({
        id: 'wbsItem.property.costPerformanceIndex',
      }),
      headerTooltip: intl.formatMessage({ id: 'evm.description.CPI' }),
      headerComponentParams: {
        template: customHeaderTemplate({
          tooltip: intl.formatMessage({ id: 'evm.description.CPI' }),
        }),
      },
      valueGetter: (params: ValueGetterParams<TRow>) => {
        if (!params.data) {
          return ''
        }
        const wbsItem = getWbsItem(params.data)
        if (!wbsItem || (wbsItem.wbsItemType?.isTask() && !isDone(wbsItem))) {
          return ''
        }
        const cumulation = getCumulation(params.data)
        const earnedValue = getTaskEarnedValue(wbsItem, cumulation)
        const actualCost = getActualHour(wbsItem, cumulation)
        return getRate(earnedValue, actualCost)
      },
    }),
    [field]
  )
  return property
}

export const useScheduleVariance = <TRow>(
  field: string,
  getWbsItem: (row: TRow) => WbsItemRow | undefined,
  getCumulation: (row: TRow) => ProjectPlanCumulation | undefined
): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      ...evmCellCommonColumnDef,
      field,
      headerName: intl.formatMessage({
        id: 'wbsItem.property.scheduleVariance',
      }),
      headerTooltip: intl.formatMessage({ id: 'evm.description.SV' }),
      headerComponentParams: {
        template: customHeaderTemplate({
          tooltip: intl.formatMessage({ id: 'evm.description.SV' }),
        }),
      },
      valueGetter: (params: ValueGetterParams<TRow>) => {
        if (!params.data) {
          return ''
        }
        const wbsItem = getWbsItem(params.data)
        if (!wbsItem || (wbsItem.wbsItemType?.isTask() && !isDone(wbsItem))) {
          return ''
        }
        const cumulation = getCumulation(params.data)
        const earnedValue = getTaskEarnedValue(wbsItem, cumulation)
        const plannedValue = getTaskPlannedValue(wbsItem, cumulation)
        return formatEvm(
          earnedValue - plannedValue,
          params.context.workloadUnitState?.hoursPerSelectedUnit || 1
        )
      },
    }),
    [field]
  )
  return property
}

export const useSchedulePerformanceIndex = <TRow>(
  field: string,
  getWbsItem: (row: TRow) => WbsItemRow | undefined,
  getCumulation: (row: TRow) => ProjectPlanCumulation | undefined
): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      ...evmRateCellCommonColumnDef,
      field,
      headerName: intl.formatMessage({
        id: 'wbsItem.property.schedulePerformanceIndex',
      }),
      headerTooltip: intl.formatMessage({ id: 'evm.description.SPI' }),
      headerComponentParams: {
        template: customHeaderTemplate({
          tooltip: intl.formatMessage({ id: 'evm.description.SPI' }),
        }),
      },
      valueGetter: (params: ValueGetterParams<TRow>) => {
        if (!params.data) {
          return ''
        }
        const wbsItem = getWbsItem(params.data)
        if (!wbsItem || (wbsItem.wbsItemType?.isTask() && !isDone(wbsItem))) {
          return ''
        }
        const cumulation = getCumulation(params.data)
        const earnedValue = getTaskEarnedValue(wbsItem, cumulation)
        const plannedValue = getTaskPlannedValue(wbsItem, cumulation)
        return getRate(earnedValue, plannedValue)
      },
    }),
    [field]
  )
  return property
}

export const useEstimateToComplete = <TRow>(
  field: string,
  getWbsItem: (row: TRow) => WbsItemRow | undefined,
  getCumulation: (row: TRow) => ProjectPlanCumulation | undefined
): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      ...evmCellCommonColumnDef,
      field,
      hide: false,
      headerName: intl.formatMessage({
        id: 'wbsItem.property.estimateToComplete',
      }),
      headerTooltip: intl.formatMessage({ id: 'evm.description.ETC' }),
      headerComponentParams: {
        template: customHeaderTemplate({
          tooltip: intl.formatMessage({ id: 'evm.description.ETC' }),
        }),
      },
      valueGetter: (params: ValueGetterParams<TRow>) => {
        if (!params.data) {
          return ''
        }
        const wbsItem = getWbsItem(params.data)
        if (!wbsItem || (wbsItem.wbsItemType?.isTask() && !isDone(wbsItem))) {
          return ''
        }
        const cumulation = getCumulation(params.data)
        return formatEvm(
          getTaskEstimateToComplete(wbsItem, cumulation),
          params.context.workloadUnitState?.hoursPerSelectedUnit || 1
        )
      },
    }),
    [field]
  )
  return property
}

export const useEstimateAtCompletion = <TRow>(
  field: string,
  getWbsItem: (row: TRow) => WbsItemRow | undefined,
  getCumulation: (row: TRow) => ProjectPlanCumulation | undefined
): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      ...evmCellCommonColumnDef,
      field,
      headerName: intl.formatMessage({
        id: 'wbsItem.property.estimateAtCompletion',
      }),
      headerTooltip: intl.formatMessage({ id: 'evm.description.EAC' }),
      headerComponentParams: {
        template: customHeaderTemplate({
          tooltip: intl.formatMessage({ id: 'evm.description.EAC' }),
        }),
      },
      valueGetter: (params: ValueGetterParams<TRow>) => {
        if (!params.data) {
          return ''
        }
        const wbsItem = getWbsItem(params.data)
        if (!wbsItem || (wbsItem.wbsItemType?.isTask() && !isDone(wbsItem))) {
          return ''
        }
        const cumulation = getCumulation(params.data)
        return formatEvm(
          getTaskEstimateAtCompletion(wbsItem, cumulation),
          params.context.workloadUnitState?.hoursPerSelectedUnit || 1
        )
      },
    }),
    [field]
  )
  return property
}

export const useVarianceAtCompletion = <TRow>(
  field: string,
  getWbsItem: (row: TRow) => WbsItemRow | undefined,
  getCumulation: (row: TRow) => ProjectPlanCumulation | undefined
): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      ...evmCellCommonColumnDef,
      field,
      headerName: intl.formatMessage({
        id: 'wbsItem.property.varianceAtCompletion',
      }),
      headerTooltip: intl.formatMessage({ id: 'evm.description.VAC' }),
      headerComponentParams: {
        template: customHeaderTemplate({
          tooltip: intl.formatMessage({ id: 'evm.description.VAC' }),
        }),
      },
      valueGetter: (params: ValueGetterParams<TRow>) => {
        if (!params.data) {
          return ''
        }
        const wbsItem = getWbsItem(params.data)
        if (!wbsItem || (wbsItem.wbsItemType?.isTask() && !isDone(wbsItem))) {
          return ''
        }
        const cumulation = getCumulation(params.data)
        const bac = getTaskEstimatedHour(wbsItem, cumulation)
        const eac = getTaskEstimateAtCompletion(wbsItem, cumulation)
        return formatEvm(
          bac - eac,
          params.context.workloadUnitState?.hoursPerSelectedUnit || 1
        )
      },
    }),
    [field]
  )
  return property
}

// Term ------
const today = DateVO.now()
export const isStartDelayed = (
  status: WbsItemStatus | undefined,
  scheduledDate: DateTerm | undefined,
  actualDate: DateTerm | undefined
): boolean => {
  if (!status) return false
  const scheduledStart = scheduledDate?.startDate
    ? new DateVO(scheduledDate.startDate)
    : undefined
  const actualStart = actualDate?.startDate
    ? new DateVO(actualDate.startDate)
    : undefined
  return !!(
    scheduledStart &&
    status === WbsItemStatus.TODO &&
    ((!actualStart && today.isAfter(scheduledStart)) ||
      (actualStart && actualStart.isAfter(scheduledStart)))
  )
}

export const isEndDelayed = (
  status: WbsItemStatus | undefined,
  scheduledDate: DateTerm | undefined,
  actualDate: DateTerm | undefined
): boolean => {
  if (!status) return false
  const scheduledEnd = scheduledDate?.endDate
    ? new DateVO(scheduledDate.endDate)
    : undefined
  const actualEnd = actualDate?.endDate
    ? new DateVO(actualDate.endDate)
    : undefined
  return !!(
    scheduledEnd &&
    ![WbsItemStatus.DONE, WbsItemStatus.DISCARD].includes(status) &&
    ((!actualEnd && today.isAfter(scheduledEnd)) ||
      (actualEnd && actualEnd.isAfter(scheduledEnd)))
  )
}

export const endDateCellEditorParams = <TRow extends ListRow>(
  getTargetDateTerm: (row: TRow | undefined) => DateTerm | undefined
) => {
  return {
    getInitialValueOnCalendar: (params: IDateCellEditorParams) => {
      const startDate = getTargetDateTerm(params.data)?.startDate
      return startDate ? dateVoService.construct(startDate) : undefined
    },
  }
}

const formatDate = (dateStr: string) =>
  dateV2Service.formatMonthAndDate(dateV2Service.newDateFromString(dateStr))

/** スプリントのセルエディタ用ラベル: 名前（m/d〜m/d） */
const formatSprintEditorLabel = (option: SprintDetail): string =>
  formatSprintLabel(option.name, option.startDate, option.endDate, formatDate)

/** スプリントをステータス順→num順でソート */
const sortSprintsByStatusAndNum = (options: SprintDetail[]): SprintDetail[] =>
  [...options].sort((a, b) => {
    const statusDiff =
      SprintStatusDisplayOrder.indexOf(a.status) -
      SprintStatusDisplayOrder.indexOf(b.status)
    if (statusDiff !== 0) return statusDiff
    return a.num - b.num
  })

// Single sprint column
export const useSprint = <TRow extends ListRow>({
  field,
  optionKey = 'sprint',
  getWbsItemProjectUuid,
  getTeamUuid,
  hide = true,
}: {
  field: string
  optionKey?: string
  getWbsItemProjectUuid: (row: TRow | undefined) => string | undefined
  getTeamUuid: (row: TRow | undefined) => string | undefined
  hide?: boolean
}): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      headerName: intl.formatMessage({ id: 'wbsItem.property.sprint' }),
      hide,
      width: 120,
      editable: true,
      type: [ColumnType.autocomplete],
      cellRenderer: EntitySearchCellRenderer,
      cellEditor: EntitySearchCellEditor,
      cellEditorParams: {
        entity: optionKey,
        optionsFilter: (option: SprintDetail, data: TRow): boolean => {
          return (
            option.projectUuid === getWbsItemProjectUuid(data) &&
            option.teamUuid === getTeamUuid(data)
          )
        },
        label: formatSprintEditorLabel,
        sortOptions: sortSprintsByStatusAndNum,
        groupBy: (option: SprintDetail) => option.status,
        defaultCollapsedGroups: [SprintStatus.FINISHED, SprintStatus.CANCELED],
      },
      suppressKeyboardEvent: params =>
        (!params.editing &&
          ['Delete', 'Backspace'].includes(params.event.key)) ||
        (params.editing && params.event.key === 'Enter'),
      filter: ClientSideSelectFilter,
      floatingFilter: true,
      filterParams: {
        optionKey,
        valueGetter: ({ node, context }) => {
          const val = objects.getValue(node.data, field)
          return typeof val === 'string' ? undefined : val // Exclude unselected data
        },
        getValue: option => (option && option.uuid ? option.uuid : ''),
        getLabel: option =>
          `[${option.status}] (${option.teamName ?? ''}) ${option.name}`,
        sortValues: (_: any, options?: SprintDetail[]) => {
          if (!options) return options
          return options.sort((a, b) => {
            if (a.status === b.status) {
              if (a.teamName === b.teamName) {
                return new DateVO(a.startDate).isAfter(new DateVO(b.startDate))
                  ? 1
                  : -1
              }
              return (a.teamName ?? '') > (b.teamName ?? '') ? 1 : -1
            }
            return (
              FilterSortOrder.indexOf(a.status) -
              FilterSortOrder.indexOf(b.status)
            )
          })
        },
      },
      comparator: (a: SprintDetail, b: SprintDetail) => {
        if (a?.name === b?.name) return 0
        return (a?.name ?? '').localeCompare(b?.name ?? '')
      },
    }),
    [field, optionKey, getWbsItemProjectUuid, getTeamUuid]
  )

  return property
}

/** スプリント配列の重複除去とnum順ソート */
const dedupeAndSortSprints = (
  sprints: SprintDetail[] | undefined
): SprintDetail[] | undefined => {
  if (!sprints) return sprints
  const uniqueSprints = sprints.filter(
    (s, index, self) => index === self.findIndex(t => t.uuid === s.uuid)
  )
  return uniqueSprints.sort((a, b) => a.num - b.num)
}

// Multiple sprints column
export const useSprints = <TRow extends ListRow>({
  field,
  hide = false,
  editable = false,
  getWbsItemProjectUuid,
  getTeamUuid,
}: {
  field: string
  hide?: boolean
  editable?: boolean
  getWbsItemProjectUuid?: (row: TRow | undefined) => string | undefined
  getTeamUuid?: (row: TRow | undefined) => string | undefined
}): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      headerName: intl.formatMessage({ id: 'wbsItem.property.sprint' }),
      hide,
      width: 120,
      editable,
      type: [ColumnType.autocomplete],
      valueSetter: editable
        ? (params: ValueSetterParams) => {
            const sprints: SprintDetail[] = params.newValue
            if (sprints) {
              params.newValue = dedupeAndSortSprints(sprints)
            }
            return defaultValueSetter(params)
          }
        : undefined,
      valueFormatter: editable
        ? (params: ValueFormatterParams) => {
            // sprints-multi-select OFF時は単体オブジェクト、ON時は配列が来るため両方に対応
            const raw = params.value
            if (!raw) return ''
            const sprints: SprintDetail[] = Array.isArray(raw) ? raw : [raw]
            if (sprints.length === 0) return ''
            return sprints.map(s => s.name).join(', ')
          }
        : undefined,
      cellRenderer: MultiAutocompleteCell.cellRenderer,
      cellRendererParams: {
        showIcon: false,
        sortValue: dedupeAndSortSprints,
      },
      cellEditor: editable ? MultiAutocompleteCellEditor : undefined,
      cellEditorParams:
        editable && getWbsItemProjectUuid && getTeamUuid
          ? {
              search: async (
                text: string,
                _projectUuid: string,
                data: TRow
              ) => {
                const teamUuid = getTeamUuid(data)
                if (!teamUuid) return []
                const projectUuid = getWbsItemProjectUuid(data)
                const results = await Sprint.searchByText(teamUuid, text)
                return results.filter(
                  (option: SprintDetail) =>
                    option.projectUuid === projectUuid &&
                    option.teamUuid === teamUuid
                )
              },
              label: formatSprintEditorLabel,
              showIcon: false,
              sortOptions: sortSprintsByStatusAndNum,
              groupBy: (option: SprintDetail) => option.status,
              defaultCollapsedGroups: [
                SprintStatus.FINISHED,
                SprintStatus.CANCELED,
              ],
            }
          : undefined,
      suppressKeyboardEvent: editable
        ? params =>
            (!params.editing &&
              ['Delete', 'Backspace'].includes(params.event.key)) ||
            (params.editing && params.event.key === 'Enter')
        : undefined,
      filter: ClientSideSelectFilter,
      floatingFilter: true,
      filterParams: {
        optionKey: 'sprint',
        valueGetter: ({ node }) => {
          const val = objects.getValue(node.data, field)
          return typeof val === 'string' ? undefined : val
        },
        getValue: option => (option && option.uuid ? option.uuid : ''),
        getLabel: option =>
          `[${option.status}] (${option.teamName ?? ''}) ${option.name}`,
        sortValues: (_: any, options?: SprintDetail[]) => {
          if (!options) return options
          return options.sort((a, b) => {
            if (a.status === b.status) {
              if (a.teamName === b.teamName) {
                return new DateVO(a.startDate).isAfter(new DateVO(b.startDate))
                  ? 1
                  : -1
              }
              return (a.teamName ?? '') > (b.teamName ?? '') ? 1 : -1
            }
            return (
              FilterSortOrder.indexOf(a.status) -
              FilterSortOrder.indexOf(b.status)
            )
          })
        },
      },
      comparator: sortSprints,
    }),
    [field, hide, editable, getWbsItemProjectUuid, getTeamUuid]
  )
  return property
}

export const useScheduledStartDate = <TRow extends ListRow>({
  field,
  getScheduledDate,
  setScheduledEndDate,
}: {
  field: string
  getScheduledDate: (row: TRow | undefined) => DateTerm | undefined
  setScheduledEndDate: (
    params: ValueSetterParams<TRow>,
    newValue: string,
    oldValue: string
  ) => void
}): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      headerName: intl.formatMessage({
        id: 'wbsItem.property.scheduledDate.start',
      }),
      width: 125,
      editable: true,
      cellEditor: DateCellEditor,
      type: [ColumnType.date],
      valueSetter: (params: ValueSetterParams<TRow>) => {
        return commonValueSetter(params, (params: ValueSetterParams<TRow>) => {
          const end = getScheduledDate(params.data)?.endDate
          if (params.newValue && end) {
            const startDate = new DateVO(params.newValue)
            const endDate = new DateVO(end)
            if (startDate.isAfter(endDate)) {
              setScheduledEndDate(
                params,
                startDate.serialize(),
                endDate.serialize()
              )
            }
          }
        })
      },
      onCellValueChanged: (event: NewValueParams) => {
        if (event.node) {
          event.api?.redrawRows({ rowNodes: [event.node] })
        }
      },
      floatingFilter: true,
    }),
    [field, getScheduledDate, setScheduledEndDate]
  )
  return property
}

export const useScheduledEndDate = <TRow extends ListRow>({
  field,
  getScheduledDate,
  setScheduledStartDate,
}: {
  field: string
  getScheduledDate: (row: TRow | undefined) => DateTerm | undefined
  setScheduledStartDate: (
    params: ValueSetterParams<TRow>,
    newValue: string,
    oldValue: string
  ) => void
}): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      headerName: intl.formatMessage({
        id: 'wbsItem.property.scheduledDate.end',
      }),
      width: 125,
      editable: true,
      cellEditor: DateCellEditor,
      type: [ColumnType.date],
      valueSetter: (params: ValueSetterParams<TRow>) => {
        return commonValueSetter(params, (params: ValueSetterParams<TRow>) => {
          const start = getScheduledDate(params.data)?.startDate
          if (params.newValue && start) {
            const startDate = new DateVO(start)
            const endDate = new DateVO(params.newValue)
            if (startDate.isAfter(endDate)) {
              setScheduledStartDate(
                params,
                endDate.serialize(),
                startDate.serialize()
              )
            }
          }
        })
      },
      floatingFilter: true,
      onCellValueChanged: (event: NewValueParams) => {
        if (event.node) {
          event.api?.redrawRows({ rowNodes: [event.node] })
        }
      },
    }),
    [field, getScheduledDate, setScheduledStartDate]
  )
  return property
}

export const useActualStartDate = <TRow extends ListRow>({
  field,
  getWbsItemStatus,
  getScheduledDate,
  getActualDate,
  setActualEndDate,
}: {
  field: string
  getWbsItemStatus: (row: TRow | undefined) => WbsItemStatus | undefined
  getScheduledDate: (row: TRow | undefined) => DateTerm | undefined
  getActualDate: (row: TRow | undefined) => DateTerm | undefined
  setActualEndDate: (
    params: ValueSetterParams<TRow>,
    newValue: string,
    oldValue: string
  ) => void
}): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      headerName: intl.formatMessage({
        id: 'wbsItem.property.actualDate.start',
      }),
      width: 125,
      editable: true,
      cellEditor: DateCellEditor,
      type: [ColumnType.date],
      valueSetter: (params: ValueSetterParams<TRow>) => {
        return commonValueSetter(params, (params: ValueSetterParams<TRow>) => {
          const end = getActualDate(params.data)?.endDate
          if (params.newValue && end) {
            const startDate = new DateVO(params.newValue)
            const endDate = new DateVO(end)
            if (startDate.isAfter(endDate)) {
              setActualEndDate(
                params,
                startDate.serialize(),
                endDate.serialize()
              )
            }
          }
        })
      },
      valueGetter: (params: ValueGetterParams<TRow>) => {
        return getActualDate(params.data)?.startDate ?? ''
      },
      cellRendererParams: {
        tooltip: (params: ICellRendererParams<TRow>): string | undefined => {
          return isStartDelayed(
            getWbsItemStatus(params.data),
            getScheduledDate(params.data),
            getActualDate(params.data)
          )
            ? intl.formatMessage({ id: 'wbs.start.delayed' })
            : undefined
        },
      },
      cellStyle: (params: CellClassParams<TRow>): CellStyle => {
        const style = { justifyContent: 'flex-end' }
        if (
          isStartDelayed(
            getWbsItemStatus(params.data),
            getScheduledDate(params.data),
            getActualDate(params.data)
          )
        ) {
          return { ...style, backgroundColor: BackgroundColor.ALERT }
        }
        return style
      },
      floatingFilter: true,
      onCellValueChanged: (event: NewValueParams) => {
        const params = { force: true }
        if (event.node) params['rowNodes'] = [event.node]
        event.api?.refreshCells(params)
      },
    }),
    [field, getWbsItemStatus, getScheduledDate, getActualDate, setActualEndDate]
  )
  return property
}

export const useActualEndDate = <TRow extends ListRow>({
  field,
  getWbsItemStatus,
  getScheduledDate,
  getActualDate,
  setActualStartDate,
}: {
  field: string
  getWbsItemStatus: (row: TRow | undefined) => WbsItemStatus | undefined
  getScheduledDate: (row: TRow | undefined) => DateTerm | undefined
  getActualDate: (row: TRow | undefined) => DateTerm | undefined
  setActualStartDate: (
    params: ValueSetterParams<TRow>,
    newValue: string,
    oldValue: string
  ) => void
}): BulkSheetProperty => {
  const property = useMemo(
    () => ({
      field,
      headerName: intl.formatMessage({
        id: 'projectPlan.actualDate.end',
      }),
      width: 125,
      editable: true,
      cellEditor: DateCellEditor,
      type: [ColumnType.date],
      cellEditorParams: endDateCellEditorParams((row: TRow | undefined) =>
        getActualDate(row)
      ),
      valueSetter: (params: ValueSetterParams<TRow>) => {
        return commonValueSetter(params, (params: ValueSetterParams<TRow>) => {
          const start = getActualDate(params.data)?.startDate
          if (params.newValue && start) {
            const startDate = new DateVO(start)
            const endDate = new DateVO(params.newValue)
            if (startDate.isAfter(endDate)) {
              setActualStartDate(
                params,
                endDate.serialize(),
                startDate.serialize()
              )
            }
          }
        })
      },
      valueGetter: (params: ValueGetterParams<TRow>) => {
        return getActualDate(params.data)?.endDate ?? ''
      },
      cellRendererParams: {
        tooltip: (params: ICellRendererParams<TRow>): string | undefined => {
          return isEndDelayed(
            getWbsItemStatus(params.data),
            getScheduledDate(params.data),
            getActualDate(params.data)
          )
            ? intl.formatMessage({ id: 'wbs.end.delayed' })
            : undefined
        },
      },
      cellStyle: (params: CellClassParams<TRow>): CellStyle => {
        const style = { justifyContent: 'flex-end' }
        if (
          isEndDelayed(
            getWbsItemStatus(params.data),
            getScheduledDate(params.data),
            getActualDate(params.data)
          )
        ) {
          return { ...style, backgroundColor: BackgroundColor.ALERT }
        }
        return style
      },
      floatingFilter: true,
      onCellValueChanged: (event: NewValueParams) => {
        const params = { force: true }
        if (event.node) params['rowNodes'] = [event.node]
        event.api?.refreshCells(params)
      },
    }),
    [
      field,
      getWbsItemStatus,
      getScheduledDate,
      getActualDate,
      setActualStartDate,
    ]
  )
  return property
}

// Ptivate variables and functions -----
const WORKLOAD_VALUE_DIGITS: number = 2
const MAX_LENGTH_ESTIMATED: number = 6
const MAX_LENGTH_ESTIMATED_STORY_POINT: number = 4
const PROGRESS_WORKLOAD_VALUE_DIGITS: number = 1
const PROGRESS_COUNT_VALUE_DIGITS: number = 0
const EVM_VALUE_DIGITS: number = 1

const WBS_ITEM_STATUS_ORDER: WbsItemStatus[] = [
  WbsItemStatus.TODO,
  WbsItemStatus.DOING,
  WbsItemStatus.REVIEW,
  WbsItemStatus.DONE,
  WbsItemStatus.DISCARD,
]

const sortWbsItemType = (
  valueA: WbsItemTypeCellValue | TicketTypeCellValue,
  valueB: WbsItemTypeCellValue | TicketTypeCellValue
) => {
  if (valueA?.typeIndex === valueB?.typeIndex) return 0
  if (valueA?.typeIndex !== undefined || valueB?.typeIndex !== undefined) {
    return (valueA?.typeIndex ?? 0) - (valueB?.typeIndex ?? 0)
  } else {
    if (valueA?.wbsItemType?.name === valueB?.wbsItemType?.name) return 0
    return (valueA?.wbsItemType?.name ?? '').localeCompare(
      valueB?.wbsItemType?.name ?? ''
    )
  }
}

const wbsItemStatusComparator = (a: WbsItemStatus, b: WbsItemStatus) =>
  WBS_ITEM_STATUS_ORDER.findIndex(s => s === a) -
  WBS_ITEM_STATUS_ORDER.findIndex(s => s === b)

interface StatusFilterParams {
  status: WbsItemStatus
  backgroundColor: string
}

const customEnumValueSetter = (params: ValueSetterParams) => {
  return commonValueSetter(params, (params: ValueSetterParams) => {
    const { customEnumCode, combinedValuePath } = params.colDef.cellEditorParams
    const options: CustomEnumValue[] = params.context[customEnumCode]

    const customEnumValue = options.find(o => o.value === params.newValue)
    const bidirection = customEnumValue?.combinations?.find(
      v => v.direction === CustomEnumCombinationDirection.BIDIRECTION
    )
    if (bidirection && combinedValuePath) {
      objects.setValue(
        params.data,
        combinedValuePath?.(bidirection.combinedEnumCode),
        bidirection.combinedValues?.[0]?.value
      )
    }
  })
}

const sortTag = (valueA: TagForWbsItem[], valueB: TagForWbsItem[]) => {
  //　don't want to get tags every time
  if (valueA === undefined && valueB === undefined) {
    return 0
  } else if (valueA === undefined) {
    return -1
  } else if (valueB === undefined) {
    return 1
  }
  if (valueA[0].projectUuid !== valueB[0].projectUuid) {
    return (valueA[0]?.projectUuid ?? '').localeCompare(
      valueB[0]?.projectUuid ?? ''
    )
  } else {
    const tags = store.getState().tag[valueA[0].projectUuid]
    if (!tags) return 0
    const minIndex = Math.min(valueA.length, valueB.length)
    for (let i = 0; i < minIndex; i++) {
      const diff =
        tags.findIndex(e => e.uuid === valueA[i]?.uuid) -
        tags.findIndex(e => e.uuid === valueB[i]?.uuid)
      if (diff !== 0) return diff
    }
  }
  return valueA.length - valueB.length
}

const useProjectMember = <TRow extends ListRow>({
  field,
  optionKey = 'member',
  getWbsItemProjectUuid,
}: {
  field: string
  optionKey: string
  getWbsItemProjectUuid?: (row: TRow | undefined) => string | undefined
}) => {
  const property = useMemo(
    () => ({
      field,
      width: 120,
      editable: true,
      type: [ColumnType.autocomplete],
      cellRenderer: EntitySearchCellRenderer,
      cellEditor: EntitySearchCellEditor,
      cellEditorParams: {
        entity: optionKey,
        optionsFilter: (option: ProjectMemberProps, data: TRow): boolean => {
          const projectUuid =
            getWbsItemProjectUuid && getWbsItemProjectUuid(data)
          return (
            !projectUuid ||
            !option.projectUuid ||
            option.projectUuid === projectUuid
          )
        },
      },
      suppressKeyboardEvent: params =>
        (!params.editing &&
          ['Delete', 'Backspace'].includes(params.event.key)) ||
        (params.editing && params.event.key === 'Enter'),
      filter: ClientSideSelectFilter,
      floatingFilter: true,
      filterParams: {
        valueGetter: ({ node, context }) => {
          const val = objects.getValue(node.data, field)
          return typeof val === 'string' ? undefined : val // Exclude unselected data
        },
        getValue: (v?: ProjectMemberProps) => v?.uuid,
        getLabel: (v?: ProjectMemberProps) => v?.name,
      },
      comparator: (valueA: ProjectMemberProps, valueB: ProjectMemberProps) =>
        (valueA?.name ?? '').localeCompare(valueB?.name ?? ''),
    }),
    [field, optionKey, getWbsItemProjectUuid]
  )
  return property
}

const sortWatchers = (
  valueA: ProjectMemberProps[],
  valueB: ProjectMemberProps[]
) => {
  valueA?.sort((a, b) => {
    return (a?.name ?? '').localeCompare(b?.name ?? '')
  })
  valueB?.sort((a, b) => {
    return (a?.name ?? '').localeCompare(b?.name ?? '')
  })
  const minIndex = Math.min(valueA?.length || 0, valueB?.length || 0)
  for (let i = 0; i < minIndex; i++) {
    const diff = (valueA[i]?.name ?? '').localeCompare(valueB[i]?.name ?? '')
    if (diff !== 0) return diff
  }
  return (valueA?.length || 0) - (valueB?.length || 0)
}

const sortSprints = (valueA: SprintDetail[], valueB: SprintDetail[]) => {
  valueA?.sort((a, b) => a.num - b.num)
  valueB?.sort((a, b) => a.num - b.num)
  const minIndex = Math.min(valueA?.length || 0, valueB?.length || 0)
  for (let i = 0; i < minIndex; i++) {
    const diff = (valueA[i]?.num ?? 0) - (valueB[i]?.num ?? 0)
    if (diff !== 0) return diff
  }
  return (valueA?.length || 0) - (valueB?.length || 0)
}

const progressCellCommonColumnDef = {
  width: 80,
  editable: false,
  hide: true,
  valueFormatter: (params: ValueFormatterParams) => {
    const digit =
      !params.context ||
      params.context?.aggregateField === AggregateField.WBS_ITEM_WORKLOAD
        ? PROGRESS_WORKLOAD_VALUE_DIGITS
        : PROGRESS_COUNT_VALUE_DIGITS
    return floatValueFormatter(params, '', digit)
  },
  cellStyle: () => {
    return {
      color: TextColor.DARK_BLACK,
      justifyContent: 'flex-end',
    }
  },
  filter: ClientSideNumberFilter,
  comparator: (valueA: number | string, valueB: number | string) => {
    const numA = typeof valueA === 'string' ? NaN : Number(valueA)
    const numB = typeof valueB === 'string' ? NaN : Number(valueB)
    if (!isFinite(numA) && !isFinite(numB)) return 0
    else if (!isFinite(numA)) return -1
    else if (!isFinite(numB)) return 1

    return numA - numB
  },
}

const progressRateCellCommonColumnDef = {
  ...progressCellCommonColumnDef,
  valueFormatter: (params: ValueFormatterParams) => {
    const numValue = parseFloat(params.value)
    if (isNaN(numValue)) return '-'
    return `${(numValue * 100).toFixed(PROGRESS_WORKLOAD_VALUE_DIGITS)}%`
  },
}

const evmCellCommonColumnDef = {
  ...progressCellCommonColumnDef,
  hide: true,
  valueFormatter: (params: ValueFormatterParams) =>
    floatValueFormatter(params, '-', EVM_VALUE_DIGITS),
}

const evmRateCellCommonColumnDef = {
  ...progressCellCommonColumnDef,
  hide: true,
  valueFormatter: (params: ValueFormatterParams) => {
    const numValue = parseFloat(params.value)
    if (isNaN(numValue)) return '-'
    return `${(numValue * 100).toFixed(EVM_VALUE_DIGITS)}%`
  },
}

const isDone = (wbsItem: WbsItemRow) => wbsItem.status === WbsItemStatus.DONE

const isDiscard = (wbsItem: WbsItemRow) =>
  wbsItem.status === WbsItemStatus.DISCARD

const isScheduledToBeDone = (wbsItem: WbsItemRow | undefined) => {
  return (
    wbsItem?.scheduledDate &&
    new DateVO(wbsItem?.scheduledDate.endDate).isSameOrBefore(DateVO.now())
  )
}

const isPreceding = (wbsItem: WbsItemRow | undefined) => {
  return (
    wbsItem?.scheduledDate &&
    new DateVO(wbsItem?.scheduledDate.endDate).isAfter(DateVO.now()) &&
    wbsItem?.status === WbsItemStatus.DONE
  )
}

const getTaskEstimatedHour = (
  wbsItem: WbsItemRow | undefined,
  cumulation: ProjectPlanCumulation | undefined
): number => {
  if (wbsItem?.wbsItemType?.isTask()) {
    return wbsItem?.estimatedWorkload?.hour || 0
  }
  if (!cumulation) return 0
  if (wbsItem?.wbsItemType?.isDeliverable()) {
    return (
      cumulation.sumTaskEstimatedHourOfDirectChildren -
      cumulation.sumTaskEstimatedHourDiscardOfDirectChildren
    )
  }
  return (
    cumulation.sumTaskEstimatedHour - cumulation.sumTaskEstimatedHourDiscard
  )
}

const getTaskEarnedValue = (
  wbsItem: WbsItemRow | undefined,
  cumulation: ProjectPlanCumulation | undefined
): number => {
  if (wbsItem?.wbsItemType?.isTask()) {
    return isDone(wbsItem) ? wbsItem?.estimatedWorkload?.hour || 0 : 0
  }

  if (!cumulation) return 0
  if (wbsItem?.wbsItemType?.isDeliverable()) {
    return cumulation.sumTaskEstimatedHourDoneOfDirectChildren
  }
  return cumulation.sumTaskEstimatedHourDone
}

const getRate = (numerator: number, denominator?: number) => {
  if (!denominator) return '-'
  return numerator / denominator
}

const getActualHour = (
  wbsItem: WbsItemRow | undefined,
  cumulation: ProjectPlanCumulation | undefined
): number => {
  if (!cumulation) return 0
  if (wbsItem?.wbsItemType?.isTask()) {
    return wbsItem.actualHour || 0
  } else if (wbsItem?.wbsItemType?.isDeliverable()) {
    return cumulation.sumActualHourOfDirectChildren
  }
  return cumulation.sumActualHour
}

const getTaskPlannedValue = (
  wbsItem: WbsItemRow | undefined,
  cumulation: ProjectPlanCumulation | undefined
): number => {
  if (wbsItem?.wbsItemType?.isTask()) {
    if (isScheduledToBeDone(wbsItem)) {
      return getTaskEstimatedHour(wbsItem, cumulation)
    }
    return 0
  }
  if (!cumulation) return 0
  if (wbsItem?.wbsItemType?.isDeliverable()) {
    return cumulation.sumTaskPlannedHourOfDirectChildren
  }
  return cumulation.sumTaskToBeCompleted
}

const getTaskCostPerformanceIndex = (
  wbsItem: WbsItemRow | undefined,
  cumulation: ProjectPlanCumulation | undefined
): number => {
  const ev = getTaskEarnedValue(wbsItem, cumulation)
  const ac = getActualHour(wbsItem, cumulation)
  return ev / ac
}

const getTaskEstimateToComplete = (
  wbsItem: WbsItemRow | undefined,
  cumulation: ProjectPlanCumulation | undefined
): number => {
  const bac = getTaskEstimatedHour(wbsItem, cumulation)
  const ev = getTaskEarnedValue(wbsItem, cumulation)

  const cpi = getTaskCostPerformanceIndex(wbsItem, cumulation)
  return (bac - ev) / cpi
}
const getTaskEstimateAtCompletion = (
  wbsItem: WbsItemRow | undefined,
  cumulation: ProjectPlanCumulation | undefined
): number => {
  const ac = getActualHour(wbsItem, cumulation)
  const etc = getTaskEstimateToComplete(wbsItem, cumulation)
  return ac + etc
}

const formatEvm = (value: number | undefined, hoursPerUnit: number) =>
  (value || 0) / hoursPerUnit

const getPrecedingTaskWorkload = (
  wbsItem: WbsItemRow | undefined,
  cumulation: ProjectPlanCumulation | undefined
): number => {
  if (wbsItem?.wbsItemType?.isTask()) {
    if (isPreceding(wbsItem)) {
      return getTaskEstimatedHour(wbsItem, cumulation)
    }
    return 0
  }
  if (!cumulation) return 0
  if (wbsItem?.wbsItemType?.isDeliverable()) {
    return cumulation.sumPrecedingTaskEstimatedHourOfDirectChildren
  }
  return cumulation.sumTaskEndPreceding
}

const getDelayedTaskWorkload = (
  wbsItem: WbsItemRow | undefined,
  cumulation: ProjectPlanCumulation | undefined
): number => {
  if (wbsItem?.wbsItemType?.isTask()) {
    if (
      isScheduledToBeDone(wbsItem) &&
      wbsItem?.status !== WbsItemStatus.DONE
    ) {
      return getTaskEstimatedHour(wbsItem, cumulation)
    }
    return 0
  }
  if (!cumulation) return 0
  if (wbsItem?.wbsItemType?.isDeliverable()) {
    return cumulation.sumDelayedTaskEstimatedHourOfDirectChildren
  }
  return cumulation.sumTaskEndDelayed
}
